import { useEffect, useState } from 'react';
import { History, Loader2, Plus, Power, RefreshCw, Target, Trash2 } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  listTrainedClassifiers,
  trainClassifier,
  deleteTrainedClassifier,
  retrainClassifier,
  setTrainedClassifierEnabled,
  evaluateTrainedClassifier,
  getTrainedClassifierHistory,
  restoreTrainedClassifierVersion,
  type TrainedAccuracyReport,
  type TrainedClassifierHistory,
  type TrainedClassifierSummary,
} from '@/lib/api';

// Mirrors the controller's limits (trained-classifiers/model.ts).
const MIN_EXAMPLES = 10;
const MAX_EXAMPLES = 5000;
const MIN_VALUE_LENGTH = 3;

function parseExamples(raw: string): string[] {
  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length >= MIN_VALUE_LENGTH);
  // The server counts distinct values only.
  return [...new Set(lines)];
}

/** Held-out values for an accuracy report: every non-blank line, as the server bounds them. */
function parseHeldOut(raw: string): string[] {
  return raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
}

function pct(v: number | null): string {
  return v === null ? 'n/a' : `${Math.round(v * 1000) / 10}%`;
}

/** One line a person can read: what the report measured. Counts only. */
export function accuracySummary(r: TrainedAccuracyReport): string {
  return (
    `version ${r.version}: precision ${pct(r.precision)}, recall ${pct(r.recall)} ` +
    `(${r.truePositives} of ${r.positives} positives found, ${r.falsePositives} of ${r.negatives} negatives matched); ` +
    `a column of the positives ${r.positivesClearScanFloor ? 'raises' : 'does not raise'} a scan finding, ` +
    `a column of the negatives ${r.negativesClearScanFloor ? 'raises one (too loose)' : 'does not'}`
  );
}

export function TrainedClassifiers({ canTrain }: { canTrain: boolean }) {
  const [rows, setRows] = useState<TrainedClassifierSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [raw, setRaw] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // Retrain target: the form above trains a NEW version of this classifier.
  const [retrainId, setRetrainId] = useState<string | null>(null);
  // Accuracy panel and history, one classifier at a time.
  const [evalId, setEvalId] = useState<string | null>(null);
  const [positives, setPositives] = useState('');
  const [negatives, setNegatives] = useState('');
  const [history, setHistory] = useState<TrainedClassifierHistory | null>(null);

  async function refresh(): Promise<void> {
    try {
      setRows(await listTrainedClassifiers());
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'failed to load trained classifiers');
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  const count = parseExamples(raw).length;
  const countOk = count >= MIN_EXAMPLES && count <= MAX_EXAMPLES;

  const retrainRow = retrainId ? rows.find((r) => r.id === retrainId) ?? null : null;

  async function train(): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      if (retrainRow) {
        const made = await retrainClassifier(retrainRow.id, parseExamples(raw));
        setRaw('');
        setRetrainId(null);
        setMsg({
          ok: true,
          text: `Retrained "${made.name}" on ${made.exampleCount} examples: version ${made.version ?? '?'} is now in use. The previous version is kept and can be restored.`,
        });
        await refresh();
        return;
      }
      const made = await trainClassifier({ name: name.trim(), examples: parseExamples(raw) });
      // The examples are dropped from the screen as soon as they are sent.
      setRaw('');
      setName('');
      setMsg({ ok: true, text: `Trained "${made.name}" on ${made.exampleCount} examples. It now votes as ${made.label}.` });
      await refresh();
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : 'training failed' });
    } finally {
      setBusy(false);
    }
  }

  async function toggle(r: TrainedClassifierSummary): Promise<void> {
    try {
      await setTrainedClassifierEnabled(r.id, r.enabled === false);
      await refresh();
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : 'switch failed' });
    }
  }

  async function evaluate(id: string): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      const { report, stored } = await evaluateTrainedClassifier(id, {
        positives: parseHeldOut(positives),
        negatives: parseHeldOut(negatives),
      });
      // The held-out values leave the screen once scored, like examples.
      setPositives('');
      setNegatives('');
      setMsg({
        ok: true,
        text: `Accuracy, ${accuracySummary(report)}.${stored ? '' : ' Not stored: the classifier was retrained meanwhile.'}`,
      });
      await refresh();
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : 'accuracy report failed' });
    } finally {
      setBusy(false);
    }
  }

  async function openHistory(id: string): Promise<void> {
    try {
      setHistory(await getTrainedClassifierHistory(id));
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : 'history failed' });
    }
  }

  async function restore(id: string, version: number): Promise<void> {
    try {
      const made = await restoreTrainedClassifierVersion(id, version);
      setMsg({ ok: true, text: `Restored version ${version} of "${made.name}" as version ${made.version ?? '?'}.` });
      setHistory(null);
      await refresh();
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : 'restore failed' });
    }
  }

  async function remove(id: string): Promise<void> {
    const row = rows.find((r) => r.id === id);
    // The examples are not kept, so a deleted classifier cannot be rebuilt (#273).
    if (
      typeof window !== 'undefined' &&
      typeof window.confirm === 'function' &&
      !window.confirm(
        `Delete "${row?.name ?? 'this classifier'}" for the whole tenant? Its examples were not kept, so it cannot be rebuilt without them. Switching it off keeps it.`,
      )
    ) {
      return;
    }
    try {
      await deleteTrainedClassifier(id);
      await refresh();
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : 'delete failed' });
    }
  }

  return (
    <GlassCard className="p-5 mb-6" data-testid="trained-classifiers">
      <CardHeader className="p-0 mb-3 flex-row items-center justify-between">
        <div>
          <CardTitle>Trained classifiers</CardTitle>
          <CardDescription clamp>
            Teach the classifier an identifier we do not know, from your own examples (one value per line, at least{' '}
            {MIN_EXAMPLES}). The examples are used once to train and are not stored; only the trained model is kept.
            The edge scanner applies every classifier that is on to each column it samples with its per-column
            PostgreSQL, MySQL, SQL Server and Oracle scanners, and to the table columns of files its file and object
            scanners read (not words in running text), inside the scanner, and a column where enough values look
            like your examples raises a finding. The SQL Server instance sweep, Db2 for i, Snowflake, BigQuery,
            Databricks, MongoDB and other SaaS scans do not apply them, nor does a laptop agent (only server
            scanners receive the classifiers). A classifier switched off keeps its open
            findings: a scan that did not apply it never closes them as no longer detected. Trained classifiers
            apply to the whole tenant, not to one business unit, so training, retraining, switching on or off and
            deleting need access to every business unit. A classifier learns a value&apos;s format, so free text
            (remarks, addresses, names) is refused. It is a pattern match, not a proof: use an
            exact-match list where a false positive matters, and run an accuracy report on held-out values.
          </CardDescription>
        </div>
        <Badge variant="glass" className="text-[10px]">{rows.length} trained</Badge>
      </CardHeader>

      {canTrain && (
        <div className="space-y-2 mb-3">
          {retrainRow ? (
            <div className="flex items-center justify-between text-[11px] glass-lite rounded px-2 py-1.5" role="note">
              <span>
                Retraining <strong>{retrainRow.name}</strong> (version {retrainRow.version ?? 1}): paste a new set of
                examples. The current version is kept and can be restored.
              </span>
              <button type="button" className="text-muted-foreground hover:underline" onClick={() => setRetrainId(null)}>
                Cancel
              </button>
            </div>
          ) : (
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Classifier name, e.g. Policy number"
              maxLength={80}
              aria-label="Classifier name"
              className="block w-full glass-lite rounded px-2 py-1.5 text-xs bg-transparent border border-border/40 focus:outline-none focus:border-primary"
            />
          )}
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            rows={5}
            spellCheck={false}
            placeholder="One example value per line"
            aria-label="Example values"
            className="block w-full glass-lite rounded px-2 py-1.5 text-xs font-mono bg-transparent border border-border/40 focus:outline-none focus:border-primary"
          />
          <div className="flex items-center justify-between">
            <span className={`text-[11px] ${countOk || count === 0 ? 'text-muted-foreground' : 'text-amber-300'}`}>
              {count} example{count === 1 ? '' : 's'} (need {MIN_EXAMPLES} to {MAX_EXAMPLES})
            </span>
            <Button size="sm" onClick={() => void train()} disabled={busy || (!retrainRow && !name.trim()) || !countOk}>
              {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
              {retrainRow ? 'Retrain classifier' : 'Train classifier'}
            </Button>
          </div>
        </div>
      )}

      {msg && (
        <div
          role={msg.ok ? 'status' : 'alert'}
          className={`text-[11px] mb-3 glass-lite rounded-md p-2 border ${
            msg.ok ? 'border-emerald-500/30 text-emerald-200' : 'border-red-500/30 text-red-300'
          }`}
        >
          {msg.text}
        </div>
      )}
      {loadError && <div role="alert" className="text-xs text-red-300 mb-2">{loadError}</div>}

      {rows.length === 0 && !loadError ? (
        <div className="text-xs text-muted-foreground py-2 text-center">No trained classifiers yet.</div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.id} className="glass-lite rounded-md px-2.5 py-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className="font-medium">{r.name}</span>
                <code className="text-[10px] text-muted-foreground font-mono">{r.label}</code>
                {r.version !== undefined && <Badge variant="glass" className="text-[8px]">v{r.version}</Badge>}
                {r.enabled === false && <Badge variant="glass" className="text-[8px]">off</Badge>}
                <Badge variant="glass" className="text-[8px] ml-auto">{r.exampleCount} examples</Badge>
                <button
                  type="button"
                  aria-label={`History of ${r.name}`}
                  onClick={() => void openHistory(r.id)}
                  className="text-muted-foreground hover:text-primary"
                >
                  <History className="h-3.5 w-3.5" />
                </button>
                {canTrain && (
                  <>
                    <button
                      type="button"
                      aria-label={r.enabled === false ? `Switch ${r.name} on` : `Switch ${r.name} off`}
                      onClick={() => void toggle(r)}
                      className="text-muted-foreground hover:text-primary"
                    >
                      <Power className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Retrain ${r.name}`}
                      onClick={() => {
                        setRetrainId(r.id);
                        setRaw('');
                      }}
                      className="text-muted-foreground hover:text-primary"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Accuracy report for ${r.name}`}
                      onClick={() => setEvalId(evalId === r.id ? null : r.id)}
                      className="text-muted-foreground hover:text-primary"
                    >
                      <Target className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${r.name}`}
                      onClick={() => void remove(r.id)}
                      className="text-muted-foreground hover:text-red-300"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
              {r.accuracy && (
                <div className="text-[10px] text-muted-foreground mt-1" data-testid={`accuracy-${r.id}`}>
                  Last accuracy report, {accuracySummary(r.accuracy)}.
                </div>
              )}
              {canTrain && evalId === r.id && (
                <div className="mt-2 space-y-1.5">
                  <p className="text-[10px] text-muted-foreground">
                    Paste held-out values the classifier was not trained on: values of this type, and values that are
                    not. They are scored once and not stored; only the counts are kept.
                  </p>
                  <textarea
                    value={positives}
                    onChange={(e) => setPositives(e.target.value)}
                    rows={3}
                    spellCheck={false}
                    aria-label="Values of this type"
                    placeholder="Values of this type, one per line"
                    className="block w-full glass-lite rounded px-2 py-1 text-xs font-mono bg-transparent border border-border/40"
                  />
                  <textarea
                    value={negatives}
                    onChange={(e) => setNegatives(e.target.value)}
                    rows={3}
                    spellCheck={false}
                    aria-label="Values not of this type"
                    placeholder="Values that are NOT this type, one per line"
                    className="block w-full glass-lite rounded px-2 py-1 text-xs font-mono bg-transparent border border-border/40"
                  />
                  <Button
                    size="sm"
                    onClick={() => void evaluate(r.id)}
                    disabled={busy || parseHeldOut(positives).length + parseHeldOut(negatives).length === 0}
                  >
                    Run accuracy report
                  </Button>
                </div>
              )}
              {history && history.current.id === r.id && (
                <div className="mt-2 space-y-1" data-testid={`history-${r.id}`}>
                  <div className="text-[10px]">
                    In use: version {history.current.version ?? 1}, {history.current.exampleCount} examples.
                  </div>
                  {history.superseded.length === 0 ? (
                    <div className="text-[10px] text-muted-foreground">No earlier versions.</div>
                  ) : (
                    history.superseded.map((v) => (
                      <div key={v.version} className="flex items-center gap-2 text-[10px]">
                        <span>
                          Version {v.version}, {v.exampleCount} examples, replaced{' '}
                          {new Date(v.supersededAt).toLocaleString()}
                          {v.accuracy ? `; ${accuracySummary(v.accuracy)}` : ''}
                        </span>
                        {canTrain && (
                          <button
                            type="button"
                            className="ml-auto text-primary hover:underline"
                            onClick={() => void restore(r.id, v.version)}
                          >
                            Restore
                          </button>
                        )}
                      </div>
                    ))
                  )}
                  <button type="button" className="text-[10px] text-muted-foreground hover:underline" onClick={() => setHistory(null)}>
                    Close
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
}
